import { Fragment, ReactNode, useState } from 'react';
import {
  UseComboboxPropGetters,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  useCombobox,
} from 'downshift';

import { Box } from '@reckon-web/box';
import { PopoverDialog } from '@reckon-web/popover';

import {
  AnchorElementType,
  SelectMenuProps,
  useSelectPopper,
} from './SelectMenu';
import {
  ComboBox,
  EmptyState,
  LoadingIndicator,
  Menu,
  MenuItem,
  SearchInput,
  SelectMenuButton,
} from './styled-components';

export const stateChangeTypes = useCombobox.stateChangeTypes;

// Main export
// ------------------------------

export type FilterMenuProps<Option> = {
  /** The value of the input. */
  inputValue?: string;
  /** Called whenever the input value changes. Use to filter the options. */
  onInputChange: (
    inputValue: string | undefined,
    restChanges: Omit<UseComboboxStateChange<Option>, 'inputValue'>
  ) => void;
  /** The input's placeholder text. */
  placeholder?: string;
  /** Use the `isLoading` property when fetching options asynchronously. */
  isLoading?: boolean;
  /** Called whenever the dropdown opens or closes. */
  onIsOpenChange?: (changes: UseComboboxStateChange<Option>) => void;
  /** Called whenever internal state changes. Use for fine-grained control of component internals. */
  stateReducer?: (
    state: UseComboboxState<Option>,
    actionAndChanges: UseComboboxStateChangeOptions<Option>
  ) => Partial<UseComboboxState<any>>;
} & SelectMenuProps<Option>;

export function FilterMenu<Option>({
  align = 'left',
  itemRenderer: consumerRenderer,
  // @ts-ignore
  itemToDisabled = (item) => Boolean(item.disabled),
  // @ts-ignore
  itemToLabel = (item) => item.label,
  // @ts-ignore
  itemToValue = (item) => item.value,
  isLoading,
  onChange,
  onClear,
  inputValue,
  onInputChange,
  onIsOpenChange,
  stateReducer: consumerStateReducer,
  options,
  placeholder = 'Filter...',
  trigger,
  value,
  // @ts-ignore
  ...props
}: FilterMenuProps<Option>) {
  // create the defaults (can't happen in destructuring because we need supporting props)
  const itemRenderer =
    consumerRenderer || ((item: Option) => itemToLabel(item));
  const stateReducer =
    consumerStateReducer ||
    ((
      state: UseComboboxState<Option>,
      actionAndChanges: UseComboboxStateChangeOptions<Option>
    ) => {
      const { changes, type } = actionAndChanges;

      if (Array.isArray(value)) {
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            return {
              ...changes,
              isOpen: true, // keep the menu open after selection
              highlightedIndex: state.highlightedIndex, // maintain the currently highlighted item
            };
        }
      }

      return changes;
    });

  // handle downshift type requirement here rather than passing on to consumers
  const itemToString = (item: Option | null) => (item ? itemToLabel(item) : '');

  // whether there's input determines what we render in the menu
  const [hasInput, setHasInput] = useState(false);

  // Ensure options is always an array
  const safeOptions = Array.isArray(options) ? options : [];

  // setup downshift (selection/navigation handling)
  const comboboxResult = useCombobox({
    items: safeOptions,
    itemToString,
    inputValue,
    onInputValueChange: ({ inputValue, ...restChanges }) => {
      setHasInput(Boolean(inputValue));
      onInputChange(inputValue, restChanges);
    },
    onIsOpenChange,
    selectedItem: null, // we don't want the input to be populated with the selected value
    stateReducer,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        onChange(selectedItem);
      }
    },
    isItemDisabled: (item, index): boolean => {
      return !isOpen || itemToDisabled(item);
    },
  });

  // Default values to avoid destructuring errors if comboboxResult is null
  const isOpen = comboboxResult?.isOpen ?? false;
  const { setAnchorElement, setPopoverElement, styles, attributes } =
    useSelectPopper({ align, isOpen });

  // Add null check for comboboxResult
  if (!comboboxResult) {
    console.error('useCombobox returned null or undefined');
    return null;
  }

  const { getInputProps, getItemProps, getMenuProps, getToggleButtonProps } =
    comboboxResult;

  // Verify that all required functions exist
  if (
    typeof getInputProps !== 'function' ||
    typeof getItemProps !== 'function' ||
    typeof getMenuProps !== 'function' ||
    typeof getToggleButtonProps !== 'function'
  ) {
    console.error('useCombobox did not return expected functions', {
      getInputProps: typeof getInputProps,
      getItemProps: typeof getItemProps,
      getMenuProps: typeof getMenuProps,
      getToggleButtonProps: typeof getToggleButtonProps,
    });
    return null;
  }

  // setup trigger (the button that invokes the menu)
  const hasValue = Boolean(
    Array.isArray(value) ? value?.length : value !== undefined
  );
  const triggerProps = getToggleButtonProps({
    ref: (element: AnchorElementType | null) => setAnchorElement(element),
    // @ts-ignore data-expanded is acceptable internal API
    // the `aria-expanded` attribute is actually applied to the element with `role="combobox"` (the input wrapper)
    // https://www.w3.org/TR/wai-aria-practices-1.1/examples/combobox/aria1.1pattern/listbox-combo.html
    'data-expanded': isOpen,
    /**
     * This is a workaround for a bug in downshift that has been logged. Revise after guidance from downshift.
     * https://github.com/downshift-js/downshift/issues/1400
     */
    tabIndex: undefined,
  });

  const triggerElement =
    typeof trigger === 'function' ? (
      trigger({ hasValue, isOpen, triggerProps })
    ) : (
      <SelectMenuButton hasValue={hasValue} onClear={onClear} {...triggerProps}>
        {trigger}
      </SelectMenuButton>
    );

  // prepare common map for default items and regular items
  const itemMap = makeItemMap({
    getItemProps,
    itemRenderer,
    itemToValue,
    value,
  });

  // prepare menu content for each state
  // use Fragment to reduce return types
  const menuUI = (() => {
    // we need a reference to the menu element to prepare accessibility
    // handlers, but we don't want the menu content to be focusable until the
    // menu is open
    if (!isOpen) {
      return null;
    }

    // replace the menu items with a loading indicator
    // NOTE: this might be too disruptive, consider moving to the input area
    if (isLoading) {
      return <LoadingIndicator />;
    }

    // no matches
    if (hasInput && !safeOptions.length) {
      return <EmptyState>No matching results</EmptyState>;
    }

    // show options when available
    if (safeOptions.length) {
      return <Fragment>{safeOptions.map(itemMap)}</Fragment>;
    }

    // unused or unaccounted state, render nothing
    return null;
  })();

  // we need a reference to the input to prepare accessibility handlers, but
  // don't want it to be focusable until the menu is open
  const inputConfig = { placeholder };
  const showDivider = Boolean(menuUI);

  return (
    <Box {...props} css={{ display: 'inline-block' }}>
      {triggerElement}
      <PopoverDialog
        ref={setPopoverElement}
        isVisible={isOpen}
        style={{ ...styles.popper, overflow: 'hidden' }} // crop highlighted options so they don't spill over the rounded corners
        {...attributes.popper}
      >
        <ComboBox showDivider={showDivider}>
          <SearchInput
            {...getInputProps(inputConfig, { suppressRefError: true })}
            onChange={(e) => {
              // Manually call the onInputValueChange since getInputProps doesn't provide onChange
              comboboxResult.setInputValue(e.target.value);
            }}
          />
        </ComboBox>
        <Menu {...getMenuProps({}, { suppressRefError: true })}>{menuUI}</Menu>
      </PopoverDialog>
    </Box>
  );
}

// Utils
// ------------------------------

type MakeItemMapProps<Option> = {
  getItemProps: UseComboboxPropGetters<Option>['getItemProps'];
  itemRenderer: (option: Option) => ReactNode;
  itemToValue: (option: Option) => string | number;
  value: Option | Array<Option> | undefined;
};

function makeItemMap<Option>({
  getItemProps,
  itemRenderer,
  itemToValue,
  value,
}: MakeItemMapProps<Option>) {
  return function itemMap(item: Option, index: number) {
    const itemValue = itemToValue(item);
    const isSelected = Array.isArray(value)
      ? value.map(itemToValue).includes(itemValue)
      : value && itemToValue(value) === itemValue;

    const itemProps = getItemProps({ item, index });

    const { onClick, ...otherProps } = itemProps;

    return (
      <MenuItem
        key={itemValue}
        isSelected={isSelected}
        {...otherProps}
        {...(onClick && { onClick: onClick as any })}
      >
        {itemRenderer(item)}
      </MenuItem>
    );
  };
}
